<template>
    <div class="survey-results">
        <survey-analytics :survey="survey"></survey-analytics>
        <div class="tw-flex tw-justify-between tw-items-center tw-my-4">
            <div class="tw-flex tw-gap-3 tw-items-center">
                <h2 class="tw-text-2xl">Survey Results</h2>
                <div class="ws-filter ws-filter-select">
                    <WSSelect v-model="selectedSource" style="min-width: 150px;" :options="[
                        {id: '', name: 'All Sources'},
                        {id: 'public', name: 'Public Survey Page'},
                        ...sources
                    ]" text-attr="name" value-attr="id" />
                </div>
            </div>
            <div class="">
                <div class="ws-button" @click="showingExport = true">Export Results</div>
            </div>
        </div>

        <div v-for="(result, index) in results" :key="result.id" class="result tw-mb-3" :class="{ 'empty': isEmpty(result.answers) }">
            <div class="head" @click="toggleShowResponse(result,$event)">
                <div class="tw-flex tw-gap-4 tw-items-center">
                    <div class="tw-flex tw-flex-grow tw-justify-between tw-items-center">
                        <div class="tw-flex tw-items-center tw-gap-2">
                            <b>{{ index + 1 }}.</b>
                            <span class="icon" :class="'type-'+result.page_icon"></span>
                            {{ result.name ?? result.page_name }}
                        </div>
                        <span class="pie-chart">
                                <doughnut-chart
                                    :width="30"
                                    :height="30"
                                    :chart-data="chartData(result)"
                                    :options="doughnutChartOptions"
                                />
                        </span>
                        <div class="stats tw-flex">
                            <span>
                                <b>{{ result.answers_count }}</b>
                                    out of
                                <b>{{ result.views }}</b>
                                    answered
                            </span>
                            <span>-</span>
                            <span>
                                <b>
                                    {{ (result.dropout_rate).toFixed(2) }}%
                                </b>
                                    dropout
                            </span>
                        </div>
                    </div>
                    <div class="toggle" :class="{ 'active': showingResponses[result.id] }"></div>
                </div>
            </div>
            <Transition name="slide">
                <div v-if="showingResponses[result.id]" class="body">
                    <div v-if="result.is_aggregate" class="answers-chart">
                        <router-link :to="{name: 'surveys-details-responses-filtered', params: {survey_id: survey.id, question: result.id, answer: key} }" v-for="(val, key, index) in sortAnswers(result.answers)" class="bar">
                            <div class="bar-value" :style="'width: '+ answerPercentage(result,val).toFixed(1) + '%'"></div>
                            <div class="labels">
                                <div class="name">{{ key }}</div>
                                <div class="details">
                                    <div v-if="result.type === 'ranking'" class="count">{{ val }} points</div>
                                    <div v-else class="count"><b>{{ val }}</b> responses</div>
                                    <div class="percentage">
                                        {{ answerPercentage(result,val).toFixed(1) }}<span>%</span>
                                    </div>
                                </div>
                            </div>
                        </router-link>
                    </div>
                    <div v-if="!result.is_aggregate" class="answers-list" >
                        <router-link :to="{name: 'surveys-details-single-response', params: {survey_id: survey.id, response: answer.response_id} }" v-for="answer in result.answers" :key="answer.id"
                                 class="answer tw-flex tw-justify-between">
                            <div class="value">{{ answer.value }}</div>
                            <div class="created">{{ answer.created_at | timeAgo }}</div>
                        </router-link>
                    </div>
                </div>
            </Transition>
        </div>

        <div v-if="!resultsLoaded" class="worksends-table-loading loadable"><div class="value-loader"></div>Retrieving your survey results....</div>

        <div v-else-if="!results.length" class="worksends-panel">
            <div class="empty-panel-message">
                <h5>This survey does not have any responses yet.</h5> In the survey summary page 
                you can get a survey link to share with your respondents, and manage additional 
                survey sources to collect results.

                <div class="empty-panel-actions">
                    <router-link class="ws-button ws-button-secondary" :to="{name: 'surveys-details', params: {survey_id: survey.id} }">
                        Survey Summary
                    </router-link>
                </div>
            </div>
        </div>

        <modal v-if="showingExport" @close="showingExport = false">
            <export-form @cancel="showingExport = false"/>
        </modal>
    </div>
</template>

<script>
import DoughnutChart from "@/components/charts/DoughnutChart";
import Modal from "@/components/modals/Modal";
import ExportForm from "@/pages/surveys/ExportForm";
import WSSelect from "@/components/elements/WSSelect.vue";
import SurveyAnalytics from "@/pages/surveys/SurveyAnalytics.vue";

export default {
    name: 'SurveyDetailsResults',
    components: {SurveyAnalytics, WSSelect, ExportForm, Modal, DoughnutChart},
    data() {
        return {
            selectedSource: '',
            showingExport: false,
            showingResponses: {},
            resultsLoaded: false,
            doughnutChartOptions: {
                cutoutPercentage: 30,
                tooltips: {
                    enabled: false
                },
                legend: {
                    display: false
                },
                elements: {
                    arc: {
                        borderWidth: 2,
                        borderColor: '#E6ECF5',
                        hoverBorderColor: '#E6ECF5'
                    }
                }
            }
        }
    },
    computed: {
        survey() {
            return this.$store.getters["surveys/selected"];
        },
        sources() {
            return this.$store.getters["surveySources/orderedList"];
        },
        results() {
            return this.$store.getters["surveyResults/orderedList"];
        },
    },
    watch: {
        selectedSource: 'fetchResults'
    },
    beforeRouteUpdate() {
        this.fetchResults();
    },
    async created() {
        if (this.$route.query.source) this.selectedSource = this.$route.query.source;
        await this.fetchResults();
        this.results.filter(result => result.is_aggregate).forEach(
            result => this.$set(this.showingResponses, result.id, true)
            );
        this.resultsLoaded = true;
        this.$store.dispatch('surveySources/getList', {})
    },
    methods: {
        answerPercentage(result,val) {
            if (result.type === 'ranking') return (100 / Object.values(result.answers).reduce((a, b) => a + b, 0) * val);
            return (val / result.answers_count * 100);
        },
        async fetchResults() {
            await this.$store.dispatch('surveyResults/getList', {source: this.selectedSource})
        },
        isEmpty(data) {
            if (
                (data.constructor === Array && !data.length) ||
                (data.constructor === Object && !Object.keys(data).length)
            ) return true;
            return false;
        },
        toggleShowResponse(result,$event) {
            if (!this.isEmpty(result.answers)) this.$set(this.showingResponses, result.id, !this.showingResponses[result.id]);
            document.getElementById('worksends-main').scroll({
                top: $event.target.offsetTop - 15,
                behavior: 'smooth'
            });
        },
        getResultDatasetSingle(result, val, key, index) {
            let max = Object.values(result.answers).reduce((a, b) => a + b, 0);
            return {
                labels: key,
                datasets: [
                    {
                        data: [val],
                        backgroundColor: function() {
                            return (index % 2 == 0) ? 'rgb(47, 154, 212)' : 'rgb(29, 89, 150)';
                        },
                    },
                    {
                        data: [max - val],
                        backgroundColor: '#E1E1E1',
                        hoverBackgroundColor: '#E1E1E1',
                    }
                ]
            }
        },
        getBarChartOptions(result) {
            let max = Object.values(result.answers).reduce((a, b) => a + b, 0);
            return {
                legend: {
                    display: false
                },
                tooltips: {
                    enabled: false
                },
                scales: {
                    xAxes: [{
                        gridLines: {
                            display: false
                        },
                        ticks: {
                            display: false,
                            beginAtZero: true,
                            min: 0,
                            max: max,
                        },
                        stacked: true
                    }],
                    yAxes: [{
                        gridLines: {
                            display: false
                        },
                        ticks: {
                            display: false
                        },
                        stacked: true,
                        barThickness: 40,
                        offset: false
                    }]
                }
            }
        },
        chartData(data) {
            return {
                datasets: [{
                    data: [
                        data.views - data.answers_count,
                        data.answers_count
                    ],
                    backgroundColor: [
                        '#CC9090',
                        '#2F9CD5'
                    ],
                    hoverBackgroundColor: [
                        '#CC9090',
                        '#2F9CD5'
                    ]
                }],
                labels: [
                    'Answered',
                    'Did Not Answer'
                ]
            };
        },
        sortAnswers(answers) {
            return Object.keys(answers).sort().reduce((a, b) => (a[b] = answers[b], a), {});
        }
    },
}
</script>

<style scoped>
    .slide-enter-active {
        transition: max-height 0.5s ease-in-out;
    }

    .slide-enter-to,
    .slide-leave {
        overflow: hidden;
        max-height: 1000px;
    }

    .slide-enter,
    .slide-leave-to {
        overflow: hidden;
        max-height: 0;
    }
</style>
<script setup>
</script>